import React, { useState, useEffect, useCallback } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import ActivityService from './activityService';
import moment from 'moment';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import { LinearProgress, TextField, Stack, Chip, Box, Paper, InputAdornment, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';

const service = new ActivityService();

export default function SourceActivityList(props) {
    const [allRows, setAllRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('datetime');

    const fetchAllData = async () => {
        setLoading(true);
        try {
            // Fetch first page only
            const response = await service.listSourceActivity();
            setAllRows(response.results);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching activity data:', error);
            setLoading(false);
        }
    };

    const applyFilters = useCallback(() => {
        let filtered = [...allRows];

        // Apply search filter
        if (searchText) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter(row =>
                row.password?.toLowerCase().includes(searchLower) ||
                row.mountpoint?.toLowerCase().includes(searchLower) ||
                row.ip?.toLowerCase().includes(searchLower) ||
                row.useragent?.toLowerCase().includes(searchLower) ||
                row.error?.toLowerCase().includes(searchLower)
            );
        }

        // Apply status filter
        if (statusFilter === 'authorized') {
            filtered = filtered.filter(row => row.authenticated === true);
        } else if (statusFilter === 'denied') {
            filtered = filtered.filter(row => row.authenticated === false);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];

            // Handle datetime sorting
            if (orderBy === 'datetime') {
                aValue = moment(aValue).valueOf();
                bValue = moment(bValue).valueOf();
            }

            if (order === 'asc') {
                return aValue < bValue ? -1 : 1;
            } else {
                return aValue > bValue ? -1 : 1;
            }
        });

        setFilteredRows(filtered);
        setPage(0); // Reset to first page when filters change
    }, [allRows, searchText, statusFilter, order, orderBy]);

    useEffect(() => {
        applyFilters();
    }, [allRows, searchText, statusFilter, order, orderBy]);

    useEffect(() => {
        fetchAllData();
    }, []);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleClear = async () => {
        setLoading(true);
        try {
            await service.clear();
            await fetchAllData();
        } catch (error) {
            console.error('Error clearing activity:', error);
            setLoading(false);
        }
    };

    const headCells = [
        { id: 'datetime', label: 'Time', sortable: true },
        { id: 'password', label: 'Password', sortable: false },
        { id: 'mountpoint', label: 'Mountpoint', sortable: true },
        { id: 'authenticated', label: 'Result', sortable: true },
        { id: 'ip', label: 'Host', sortable: true },
        { id: 'useragent', label: 'Application', sortable: false },
    ];

    // Get current page rows
    const displayRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading && allRows.length === 0) {
        return (
            <Box sx={{ width: '100%', mt: 2 }}>
                <LinearProgress />
                <Box sx={{ textAlign: 'center', mt: 2 }}>Loading activity data...</Box>
            </Box>
        );
    }

    return (
        <React.Fragment>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
                <TextField
                    size="small"
                    placeholder="Search activity..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    sx={{ flexGrow: 1, maxWidth: 400 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                        startAdornment={<FilterListIcon sx={{ ml: 1, mr: -0.5 }} />}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="authorized">Authorized</MenuItem>
                        <MenuItem value="denied">Denied</MenuItem>
                    </Select>
                </FormControl>
                <Button
                    variant="outlined"
                    onClick={fetchAllData}
                    startIcon={<RefreshIcon />}
                >
                    Refresh
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClear}
                    startIcon={loading ? <CircularProgress size={16} /> : <ClearAllIcon />}
                    disabled={loading}
                >
                    Clear All
                </Button>
            </Stack>

            <Chip
                label={`${filteredRows.length} ${filteredRows.length === 1 ? 'entry' : 'entries'} found`}
                size="small"
                sx={{ mb: 1 }}
            />

            <Paper elevation={1}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {headCells.map((headCell) => (
                                    <TableCell key={headCell.id} align="left">
                                        {headCell.sortable ? (
                                            <TableSortLabel
                                                active={orderBy === headCell.id}
                                                direction={orderBy === headCell.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(headCell.id)}
                                            >
                                                {headCell.label}
                                            </TableSortLabel>
                                        ) : (
                                            headCell.label
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        No activity records found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                displayRows.map((row, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        hover
                                    >
                                        <TableCell>
                                            {moment(row.datetime, moment.ISO_8601).format("MM/DD/YYYY hh:mm:ss")}
                                        </TableCell>
                                        <TableCell>{row.password}</TableCell>
                                        <TableCell>
                                            <Chip label={row.mountpoint} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            {row.authenticated ? (
                                                <Chip label="Authorized" color="success" size="small" />
                                            ) : (
                                                <Chip
                                                    label={`Denied${row.error ? `: ${row.error}` : ''}`}
                                                    color="error"
                                                    size="small"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {row.ip === "127.0.0.1" ? "Local GNSS Receiver" : row.ip}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {row.useragent}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[25, 50, 100, 200]}
                    component="div"
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
            {loading && <LinearProgress sx={{ mt: 1 }} />}
        </React.Fragment>
    );
}
